import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorDialog } from '@/components/ui/ErrorDialog';
import { useAuth } from '@/lib/auth';

import {
  adminCreatePackageAlias,
  adminDeletePackageAlias,
  fetchPackages,
  fetchAdminPackages,
  fetchIngestStatus,
  fetchIndexRequests,
  queryPackage,
  adminListPackageAliases,
  adminReindex,
  adminIngest,
  adminResolveAliases,
  adminUpdatePackageAlias,
  adminUpdateOverview,
  adminSetHidden,
  adminPausePackage,
  adminResumePackage,
  fetchPackageHealth,
  adminDeletePackage,
  type AdminResolveAliasesResponse,
} from '../api';
import { navigateTo } from '../router';
import type {
  IndexRequest,
  PackageAlias,
  PackageIndexHealth,
  PackagePauseKey,
  PackageSummary,
  QueryResult,
} from '../types';
import {
  buildTreeRoute,
  displayPackageName,
  formatAnswerCitations,
  formatAskElapsed,
  formatDuration,
  formatIndexedAt,
  isDocsOnlyQuality,
  relativeTime,
} from '../utils';
import { useLiveElapsed } from '../hooks';
import { RegistryBadge } from '../components/RegistryBadge';
import { AnalyticsPanel } from '../components/AnalyticsPanel';
import { IndexMetricsPanel } from '../components/IndexMetricsPanel';
import { PackageHealthPanel } from '../components/PackageHealthPanel';
import { CommonPackageRequestsPanel } from '../components/RequestPanels';
import { calculateTodayIndexingStats } from '../admin-indexing-stats';

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
type SortKey =
  | 'name-asc'
  | 'name-desc'
  | 'newest'
  | 'oldest'
  | 'versions'
  | 'cost-high'
  | 'cost-low'
  | 'quality-high'
  | 'quality-low';

const RELATION_TYPE_OPTIONS = [
  { value: 'alias', label: 'alias' },
  { value: 'canonical-repo', label: 'canonical-repo' },
] as const;

const VERIFICATION_METHOD_OPTIONS = [
  { value: '', label: '— unverified —' },
  { value: 'repo-manifest', label: 'repo-manifest' },
  { value: 'tarball-manifest-match', label: 'tarball-manifest-match' },
  { value: 'llm-search', label: 'llm-search' },
  { value: 'admin', label: 'admin' },
] as const;

const ALIAS_REGISTRY_OPTIONS = [
  { value: 'npm', label: 'npm' },
  { value: 'pypi', label: 'PyPI' },
  { value: 'pub', label: 'pub.dev' },
] as const;

// ── Global Ask Bar ────────────────────────────────────────────────────────────

function SparklesIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

interface AskResult {
  question: string;
  result: QueryResult;
  elapsedMs: number;
}

function GlobalAskBar() {
  const [question, setQuestion] = useState('');
  const [queryRegistry, setQueryRegistry] = useState('npm');
  const [queryPackageName, setQueryPackageName] = useState('');
  const [queryVersion, setQueryVersion] = useState('');
  const [isAsking, setIsAsking] = useState(false);
  const [askResult, setAskResult] = useState<AskResult | null>(null);
  const [askError, setAskError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);
  const liveElapsedMs = useLiveElapsed(isAsking);

  const handleAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !queryPackageName.trim() || !queryVersion.trim() || isAsking) return;

    const startedAt = Date.now();
    setIsAsking(true);
    setAskError(null);
    setExpanded(true);

    try {
      const result = await queryPackage({
        question: question.trim(),
        registry: queryRegistry,
        name: queryPackageName.trim(),
        version: queryVersion.trim(),
      });
      setAskResult({ question: question.trim(), result, elapsedMs: Date.now() - startedAt });
      setQuestion('');
      // Scroll result into view
      setTimeout(
        () => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
        100
      );
    } catch (err) {
      setAskError(err instanceof Error ? err.message : 'Failed to get answer');
    } finally {
      setIsAsking(false);
    }
  };

  return (
    <div className="lc-surface overflow-hidden border border-accent/40">
      <form onSubmit={handleAsk} className="relative">
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="text-accent">
            <SparklesIcon />
          </span>
          <select
            value={queryRegistry}
            onChange={(event) => setQueryRegistry(event.target.value)}
            disabled={isAsking}
            aria-label="Registry"
            className="w-24 border border-rule bg-bg px-2 py-1.5 text-xs text-link outline-none"
          >
            <option value="npm">npm</option>
            <option value="pypi">PyPI</option>
            <option value="pub">pub.dev</option>
            <option value="crates">crates.io</option>
            <option value="maven">Maven</option>
          </select>
          <input
            value={queryPackageName}
            onChange={(event) => setQueryPackageName(event.target.value)}
            placeholder="Package"
            disabled={isAsking}
            aria-label="Package name"
            className="w-36 border border-rule bg-bg px-2 py-1.5 text-xs text-link placeholder-dim outline-none"
          />
          <input
            value={queryVersion}
            onChange={(event) => setQueryVersion(event.target.value)}
            placeholder="Version"
            disabled={isAsking}
            aria-label="Package version"
            className="w-24 border border-rule bg-bg px-2 py-1.5 text-xs text-link placeholder-dim outline-none"
          />
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about this exact version…"
            disabled={isAsking}
            className="flex-1 bg-transparent text-sm text-ink placeholder-dim outline-none"
          />
          {isAsking ? (
            <div className="flex items-center gap-2 text-xs text-accent">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" />
              Searching…
              {liveElapsedMs != null && (
                <span className="font-mono text-2xs text-accent/80">
                  {formatAskElapsed(liveElapsedMs)}
                </span>
              )}
            </div>
          ) : (
            <Button
              type="submit"
              size="sm"
              disabled={!question.trim() || !queryPackageName.trim() || !queryVersion.trim()}
            >
              Ask
            </Button>
          )}
        </div>
      </form>

      {/* Error */}
      {askError && (
        <div className="border-t border-del/30 bg-del/15 px-4 py-2.5 text-xs text-del">
          {askError}
          <button onClick={() => setAskError(null)} className="ml-2 underline">
            dismiss
          </button>
        </div>
      )}

      {/* Result */}
      {askResult && expanded && (
        <div ref={resultRef} className="border-t border-accent/40">
          {/* Question echo */}
          <div className="flex items-start gap-3 bg-accent-soft px-4 py-3">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-3xs font-bold text-accent">
              Q
            </span>
            <p className="text-sm font-medium text-link">{askResult.question}</p>
            <button
              onClick={() => setExpanded(false)}
              className="ml-auto shrink-0 rounded p-1 text-dim hover:bg-bg transition-colors"
              title="Collapse"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Source badge(s) */}
          {askResult.result.resolvedPackage && (
            <div className="flex items-center gap-1.5 flex-wrap px-4 py-2 bg-bg border-b border-rule-soft">
              <span className="text-3xs font-medium uppercase tracking-wider text-dim">
                Sources
              </span>
              {askResult.result.resolvedPackage.split(', ').map((src) => (
                <span
                  key={src}
                  className="rounded bg-accent-soft px-1.5 py-0.5 font-mono text-3xs font-medium text-accent"
                >
                  {src}
                </span>
              ))}
              <span className="rounded bg-bg px-1.5 py-0.5 text-3xs text-dim">
                {askResult.result.chunks.length} chunks
              </span>
              <span
                className="ml-auto rounded bg-bg px-1.5 py-0.5 font-mono text-3xs text-dim"
                title="Time to answer"
              >
                {formatAskElapsed(askResult.elapsedMs)}
              </span>
            </div>
          )}

          {/* Answer */}
          <div className="px-4 py-4">
            <div
              className="prose prose-sm prose-zinc dark:prose-invert max-w-none text-sm leading-relaxed
              prose-headings:font-semibold prose-headings:tracking-tight prose-headings:mt-5 prose-headings:mb-2
              prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
              prose-a:text-accent prose-a:no-underline hover:prose-a:underline
              prose-strong:text-ink
              prose-ul:my-3 prose-ol:my-3 prose-li:my-1
              [&_pre]:!bg-transparent [&_pre]:!p-0 [&_pre]:!my-3 [&_pre]:overflow-x-auto
              [&_.hljs]:p-4 [&_.hljs]:text-xs [&_.hljs]:leading-relaxed
              [&_code:not(pre_code)]:rounded [&_code:not(pre_code)]:bg-bg [&_code:not(pre_code)]:px-1.5 [&_code:not(pre_code)]:py-0.5 [&_code:not(pre_code)]:text-xs [&_code:not(pre_code)]:font-mono [&_code:not(pre_code)]:text-accent
              [&_p]:mb-3 last:[&_p]:mb-0"
            >
              <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {formatAnswerCitations(askResult.result.answer)}
              </Markdown>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed indicator */}
      {askResult && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="flex w-full items-center gap-2 border-t border-rule-soft px-4 py-2 text-xs text-dim hover:text-link transition-colors"
        >
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-good/15 text-3xs font-bold text-good">
            A
          </span>
          <span className="truncate">{askResult.result.answer.slice(0, 80)}…</span>
          <span className="ml-auto shrink-0">Show</span>
        </button>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      className="h-4 w-4 text-dim"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
      />
    </svg>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? 'border-accent bg-accent-soft text-accent'
          : 'border-rule bg-panel text-link hover:bg-bg'
      }`}
    >
      {children}
    </button>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
      />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ReindexIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function UploadIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0-12l-4 4m4-4l4 4"
      />
    </svg>
  );
}

function OverviewIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
      />
    </svg>
  );
}

function TrashIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function EyeIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function EyeOffIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  );
}

function DatabaseIcon({ className = 'h-3 w-3' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 7v10c0 1.657 3.582 3 8 3s8-1.343 8-3V7M4 7c0 1.657 3.582 3 8 3s8-1.343 8-3M4 7c0-1.657 3.582-3 8-3s8 1.343 8 3m0 5c0 1.657-3.582 3-8 3s-8-1.343-8-3"
      />
    </svg>
  );
}

function LinkIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.828 10.172a4 4 0 015.656 0 4 4 0 010 5.656l-3 3a4 4 0 01-5.656 0M10.172 13.828a4 4 0 01-5.656 0 4 4 0 010-5.656l3-3a4 4 0 015.656 0"
      />
    </svg>
  );
}

function registrySectionRank(registry: string): number {
  if (registry === 'github') return 0;
  if (registry === 'gitlab') return 1;
  if (registry === 'npm') return 2;
  if (registry === 'pypi') return 3;
  if (registry === 'pub') return 4;
  if (registry === 'local') return 5;
  return 99;
}

interface PackageGroup {
  registry: string;
  name: string;
  versions: PackageSummary[];
}

interface AliasDraft {
  registry: string;
  name: string;
  version: string;
  relationType: string;
  verificationMethod: string;
  sourceSubpath: string;
  resolvedCommitSha: string;
}

function createAliasDraft(alias?: PackageAlias): AliasDraft {
  return {
    registry: alias?.registry ?? '',
    name: alias?.name ?? '',
    version: alias?.version ?? '',
    relationType: alias?.relation_type ?? 'alias',
    verificationMethod: alias?.verification_method ?? '',
    sourceSubpath: alias?.source_subpath ?? '',
    resolvedCommitSha: alias?.resolved_commit_sha ?? '',
  };
}

function sortVersionsDesc(versions: PackageSummary[]): PackageSummary[] {
  return [...versions].sort((a, b) => {
    const aParts = a.version.split('.').map(Number);
    const bParts = b.version.split('.').map(Number);
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const diff = (bParts[i] ?? 0) - (aParts[i] ?? 0);
      if (diff !== 0) return diff;
    }
    return 0;
  });
}

function groupPackages(packages: PackageSummary[]): PackageGroup[] {
  const map = new Map<string, PackageGroup>();
  for (const pkg of packages) {
    const key = `${pkg.registry}:${pkg.name}`;
    let group = map.get(key);
    if (!group) {
      group = { registry: pkg.registry, name: pkg.name, versions: [] };
      map.set(key, group);
    }
    group.versions.push(pkg);
  }
  const groups = Array.from(map.values());
  for (const group of groups) {
    group.versions = sortVersionsDesc(group.versions);
  }
  return groups;
}

// The most recent time ANY version in a group was indexed, as a timestamp.
// `versions[0]` is the highest semver (from sortVersionsDesc), which is not
// necessarily the most-recently-indexed version — a back-filled or patch
// release can be indexed later. Recency-based sorting and the "indexed X ago"
// label must use this max, not versions[0].indexed_at.
function groupLastIndexedTime(group: PackageGroup): number {
  return group.versions.reduce((max, v) => {
    const t = new Date(v.indexed_at).getTime();
    return Number.isNaN(t) ? max : Math.max(max, t);
  }, 0);
}

// ISO string of the group's most-recently-indexed version, for display.
function groupLastIndexedAt(group: PackageGroup): string {
  let latest = group.versions[0];
  for (const v of group.versions) {
    if (new Date(v.indexed_at).getTime() > new Date(latest.indexed_at).getTime()) latest = v;
  }
  return latest.indexed_at;
}

function sortGroups(groups: PackageGroup[], sort: SortKey): PackageGroup[] {
  return [...groups].sort((a, b) => {
    switch (sort) {
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'newest':
        return groupLastIndexedTime(b) - groupLastIndexedTime(a);
      case 'oldest':
        return groupLastIndexedTime(a) - groupLastIndexedTime(b);
      case 'versions':
        return b.versions.length - a.versions.length;
      case 'cost-high':
        return (b.versions[0].index_cost ?? -1) - (a.versions[0].index_cost ?? -1);
      case 'cost-low':
        return (
          (a.versions[0].index_cost ?? Number.POSITIVE_INFINITY) -
          (b.versions[0].index_cost ?? Number.POSITIVE_INFINITY)
        );
      case 'quality-high':
        return (b.versions[0].index_quality ?? -1) - (a.versions[0].index_quality ?? -1);
      case 'quality-low':
        return (
          (a.versions[0].index_quality ?? Number.POSITIVE_INFINITY) -
          (b.versions[0].index_quality ?? Number.POSITIVE_INFINITY)
        );
    }
  });
}

interface PackageCardProps {
  group: PackageGroup;
  shade: boolean;
  isAdmin: boolean;
  adminToken: string | null;
  onAdminAction: (action: AdminAction) => Promise<number | void>;
  onAliasesChanged: () => Promise<void>;
  onAliasError: (message: string) => void;
  ingestStatus: Record<string, number>;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** The public package's indexing health, when it is paused or failing. */
  health?: PackageIndexHealth;
  onPauseToggle?: () => Promise<void>;
}

/** Key of a public package in the health map (pauses match names case-insensitively). */
const healthKey = (registry: string, name: string) => `${registry}:${name.toLowerCase()}`;

type AdminAction =
  | { type: 'reindex'; registry: string; name: string; version: string }
  | { type: 'ingest'; registry: string; name: string; version: string }
  | { type: 'overview'; registry: string; name: string; version: string }
  | { type: 'hide'; registry: string; name: string; version: string; hidden: boolean }
  // Addressed by package id, not identity: the admin catalog spans every org,
  // and registry/name/version alone cannot say which org's row is meant.
  | { type: 'delete'; packageId: string; registry: string; name: string; version: string };

function AliasManager({
  pkg,
  adminToken,
  onChanged,
  onError,
}: {
  pkg: PackageSummary;
  adminToken: string | null;
  onChanged: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [aliases, setAliases] = useState<PackageAlias[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AliasDraft>(createAliasDraft());
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [pendingResolve, setPendingResolve] = useState(false);
  const [resolveResult, setResolveResult] = useState<AdminResolveAliasesResponse | null>(null);

  const canResolve =
    !!adminToken &&
    !!pkg.id &&
    (pkg.registry === 'github' || pkg.registry === 'gitlab') &&
    !!pkg.source_url;

  const handleResolveAliases = async () => {
    if (!adminToken || !pkg.id) return;
    setIsResolving(true);
    try {
      const result = await adminResolveAliases(pkg.id, { useLlmFallback: true }, adminToken);
      setResolveResult(result);
      await loadAliases();
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to resolve aliases');
    } finally {
      setIsResolving(false);
      setPendingResolve(false);
    }
  };

  const loadAliases = useCallback(async () => {
    if (!adminToken || !pkg.id) return;
    setIsLoading(true);
    try {
      const nextAliases = await adminListPackageAliases(pkg.id, adminToken);
      setAliases(nextAliases);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load package aliases');
    } finally {
      setIsLoading(false);
    }
  }, [adminToken, onError, pkg.id]);

  const toggleExpanded = () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    if (nextExpanded && aliases.length === 0 && !isLoading) {
      void loadAliases();
    }
  };

  const resetEditor = () => {
    setEditingId(null);
    setDraft(createAliasDraft());
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!adminToken || !pkg.id) return;
    if (!draft.registry.trim() || !draft.name.trim() || !draft.version.trim()) return;

    setIsSaving(true);
    try {
      const payload = {
        packageId: pkg.id,
        registry: draft.registry.trim(),
        name: draft.name.trim(),
        version: draft.version.trim(),
        relationType: draft.relationType.trim() || 'alias',
        verificationMethod: draft.verificationMethod.trim() || null,
        sourceSubpath: draft.sourceSubpath.trim() || null,
        resolvedCommitSha: draft.resolvedCommitSha.trim() || null,
      };
      if (editingId) {
        await adminUpdatePackageAlias(editingId, payload, adminToken);
      } else {
        await adminCreatePackageAlias(payload, adminToken);
      }
      await loadAliases();
      await onChanged();
      resetEditor();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to save package alias');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (aliasId: string) => {
    if (!adminToken) return;
    setIsSaving(true);
    try {
      await adminDeletePackageAlias(aliasId, adminToken);
      await loadAliases();
      await onChanged();
      if (editingId === aliasId) resetEditor();
      setPendingDeleteId(null);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete package alias');
    } finally {
      setIsSaving(false);
    }
  };

  const pendingDeleteAlias = pendingDeleteId
    ? (aliases.find((a) => a.id === pendingDeleteId) ?? null)
    : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggleExpanded}
          className="rounded px-1.5 py-0.5 text-dim hover:bg-accent-soft hover:text-accent transition-colors"
        >
          {expanded ? '▾ Aliases' : '▸ Aliases'}
        </button>
        <span className="text-3xs uppercase tracking-wide text-dim">
          {pkg.aliases?.length ?? aliases.length} rows
        </span>
        {canResolve && (
          <button
            type="button"
            onClick={() => setPendingResolve(true)}
            disabled={isResolving}
            title="Re-resolve aliases by scanning repo manifests"
            className="ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-2xs font-medium text-accent hover:bg-accent-soft disabled:opacity-50"
          >
            <LinkIcon />
            {isResolving ? 'Resolving…' : 'Resolve aliases'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="space-y-3 border border-rule-soft bg-panel p-3">
          {isLoading ? (
            <p className="text-xs text-dim">Loading aliases…</p>
          ) : aliases.length > 0 ? (
            <div className="space-y-2">
              {aliases.map((alias) => (
                <div
                  key={alias.id}
                  className="space-y-1 rounded border border-rule-soft bg-bg px-2.5 py-2 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-accent">{alias.registry}</span>
                    <span className="text-link">{alias.name}</span>
                    <span className="font-mono text-dim">{alias.version}</span>
                    {alias.relation_type ? (
                      <span className="rounded bg-accent-soft px-1.5 py-0.5 text-3xs font-medium text-accent">
                        {alias.relation_type}
                      </span>
                    ) : null}
                    {alias.verification_method ? (
                      <span
                        className="rounded bg-good/15 px-1.5 py-0.5 text-3xs font-medium text-good"
                        title="verification method"
                      >
                        {alias.verification_method}
                      </span>
                    ) : (
                      <span
                        className="rounded bg-bg px-1.5 py-0.5 text-3xs font-medium text-dim"
                        title="verification method"
                      >
                        unverified
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(alias.id);
                          setDraft(createAliasDraft(alias));
                        }}
                        className="rounded px-1.5 py-0.5 text-dim hover:bg-accent-soft hover:text-accent"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(alias.id)}
                        className="rounded px-1.5 py-0.5 text-dim hover:bg-del/10 hover:text-del"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {alias.source_subpath || alias.resolved_commit_sha ? (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-0.5 text-3xs text-dim">
                      {alias.source_subpath ? (
                        <span>
                          <span className="text-dim">subpath:</span>{' '}
                          <code className="font-mono">{alias.source_subpath}</code>
                        </span>
                      ) : null}
                      {alias.resolved_commit_sha ? (
                        <span>
                          <span className="text-dim">sha:</span>{' '}
                          <code className="font-mono">
                            {alias.resolved_commit_sha.slice(0, 10)}
                          </code>
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-dim">No aliases for this repository version yet.</p>
          )}

          <form onSubmit={handleSubmit} className="space-y-2 border border-dashed border-rule p-3">
            <div className="flex items-center justify-between">
              <p className="text-2xs font-medium uppercase tracking-wide text-dim">
                {editingId ? 'Update alias' : 'Add alias'}
              </p>
              {editingId ? (
                <button
                  type="button"
                  onClick={resetEditor}
                  className="text-xs text-dim hover:text-link"
                >
                  Cancel
                </button>
              ) : null}
            </div>
            <div className="grid gap-2 md:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Registry
                </span>
                <select
                  value={draft.registry}
                  onChange={(e) =>
                    setDraft((current) => ({ ...current, registry: e.target.value }))
                  }
                  className="lc-input py-1.5 text-xs"
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  {ALIAS_REGISTRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">Name</span>
                <input
                  value={draft.name}
                  onChange={(e) => setDraft((current) => ({ ...current, name: e.target.value }))}
                  placeholder="e.g. react"
                  className="lc-input py-1.5 text-xs"
                />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Version
                </span>
                <input
                  value={draft.version}
                  onChange={(e) => setDraft((current) => ({ ...current, version: e.target.value }))}
                  placeholder="e.g. 1.2.3"
                  className="lc-input py-1.5 text-xs"
                />
              </label>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Relation
                </span>
                <select
                  value={draft.relationType || 'alias'}
                  onChange={(e) =>
                    setDraft((current) => ({ ...current, relationType: e.target.value }))
                  }
                  className="lc-input py-1.5 text-xs"
                >
                  {RELATION_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Verification
                </span>
                <select
                  value={draft.verificationMethod}
                  onChange={(e) =>
                    setDraft((current) => ({ ...current, verificationMethod: e.target.value }))
                  }
                  className="lc-input py-1.5 text-xs"
                >
                  {VERIFICATION_METHOD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Source subpath <span className="normal-case text-dim">(optional)</span>
                </span>
                <input
                  value={draft.sourceSubpath}
                  onChange={(e) =>
                    setDraft((current) => ({ ...current, sourceSubpath: e.target.value }))
                  }
                  placeholder="e.g. packages/core"
                  className="lc-input py-1.5 text-xs"
                />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Commit SHA <span className="normal-case text-dim">(optional)</span>
                </span>
                <input
                  value={draft.resolvedCommitSha}
                  onChange={(e) =>
                    setDraft((current) => ({ ...current, resolvedCommitSha: e.target.value }))
                  }
                  placeholder="full SHA"
                  className="lc-input py-1.5 text-xs font-mono"
                />
              </label>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                disabled={
                  isSaving || !draft.registry.trim() || !draft.name.trim() || !draft.version.trim()
                }
              >
                {editingId ? 'Update alias' : 'Add alias'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {pendingDeleteAlias ? (
        <ConfirmDialog
          title="Delete repository alias?"
          message={`This will remove the alias ${pendingDeleteAlias.registry}/${pendingDeleteAlias.name}@${pendingDeleteAlias.version}. This cannot be undone.`}
          confirmLabel="Delete alias"
          tone="danger"
          isConfirming={isSaving}
          onConfirm={() => void handleDelete(pendingDeleteAlias.id)}
          onClose={() => setPendingDeleteId(null)}
        />
      ) : null}

      {pendingResolve ? (
        <ConfirmDialog
          title={`Resolve aliases for ${pkg.name}@${pkg.version}?`}
          message={
            (pkg.aliases?.length ?? 0) > 0
              ? `This scans the repo manifests and REPLACES the ${pkg.aliases?.length} existing alias${(pkg.aliases?.length ?? 0) === 1 ? '' : 'es'} with freshly verified matches.`
              : 'This scans the repo manifests and adds any verified repository aliases it finds.'
          }
          confirmLabel="Resolve"
          isConfirming={isResolving}
          onConfirm={() => void handleResolveAliases()}
          onClose={() => setPendingResolve(false)}
        />
      ) : null}

      {resolveResult ? (
        <ResolveResultDialog result={resolveResult} onClose={() => setResolveResult(null)} />
      ) : null}
    </div>
  );
}

/**
 * Confirmation for deleting one indexed version.
 *
 * Deletion is irreversible and expensive to undo — re-indexing spends LLM and
 * embedding credits — and this button sits in a dense row next to Hide, which
 * is trivially reversible. Typing the word is what separates the two: it makes
 * the destructive action impossible to reach by the same reflex that reaches
 * the safe one.
 */
function DeleteVersionDialog({
  pkg,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  pkg: PackageSummary;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim().toLowerCase() === 'delete';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={busy ? undefined : onClose}
      />
      <div className="relative w-full max-w-md overflow-hidden border border-rule bg-panel shadow-2xl">
        <div className="border-b border-rule-soft px-5 py-3">
          <h3 className="text-base font-semibold text-ink">Delete this version?</h3>
          <p className="mt-0.5 font-mono text-xs text-dim">
            {pkg.registry} / {pkg.name}@{pkg.version}
          </p>
        </div>
        <div className="space-y-3 px-5 py-4 text-xs text-link">
          <p>
            This permanently removes the package row, its search vectors, call and import edges, the
            chunk mapping, and both payload objects in storage.
          </p>
          <p className="text-dim">
            It cannot be undone. Restoring this version means re-indexing it, which spends LLM and
            embedding credits.
          </p>
          <div>
            <label htmlFor="delete-confirm" className="mb-1 block text-2xs text-dim">
              Type <span className="font-mono font-semibold text-ink">delete</span> to confirm
            </label>
            <input
              id="delete-confirm"
              autoFocus
              value={typed}
              disabled={busy}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && confirmed && !busy) onConfirm();
              }}
              className="w-full rounded border border-rule bg-bg px-2 py-1.5 font-mono text-xs text-ink outline-none focus:border-del disabled:opacity-40"
              placeholder="delete"
              autoComplete="off"
            />
          </div>
          {error && (
            <p className="rounded border border-del/30 bg-del/15 px-2 py-1.5 text-2xs text-del">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-rule-soft px-5 py-3">
          <Button size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <button
            onClick={onConfirm}
            disabled={!confirmed || busy}
            className="inline-flex items-center gap-1.5 rounded bg-del px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-del/85 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Deleting…
              </>
            ) : (
              'Delete version'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResolveResultDialog({
  result,
  onClose,
}: {
  result: AdminResolveAliasesResponse;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl overflow-hidden border border-rule bg-panel shadow-2xl">
        <div className="border-b border-rule-soft px-5 py-3">
          <h3 className="text-base font-semibold text-ink">Alias resolution complete</h3>
          <p className="mt-0.5 text-xs text-dim">
            {result.registry} / {result.name}@{result.version}
          </p>
        </div>
        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 py-4 text-xs">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Removed" value={result.removed} tone="amber" />
            <Stat label="Written" value={result.written} tone="emerald" />
            <Stat label="Manifests" value={result.stats.manifestsFound} tone="neutral" />
            <Stat label="Candidates" value={result.stats.candidatesDeduped} tone="neutral" />
          </div>

          {result.verified.length > 0 && (
            <div>
              <p className="mb-1 text-2xs font-medium uppercase tracking-wide text-good">
                Verified ({result.verified.length})
              </p>
              <div className="space-y-1">
                {result.verified.map((v, i) => (
                  <div key={i} className="rounded border border-good/30 bg-good/15 px-2 py-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-good">{v.registry}</span>
                      <span className="text-link">{v.name}</span>
                      {v.version ? <span className="font-mono text-dim">{v.version}</span> : null}
                      <span className="ml-auto rounded bg-good/15 px-1.5 py-0.5 text-3xs font-medium text-good">
                        {v.verificationMethod}
                      </span>
                    </div>
                    {v.note ? <p className="mt-0.5 text-3xs text-dim">{v.note}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.skipped.length > 0 && (
            <div>
              <p className="mb-1 text-2xs font-medium uppercase tracking-wide text-dim">
                Skipped ({result.skipped.length})
              </p>
              <div className="space-y-1">
                {result.skipped.map((s, i) => (
                  <div key={i} className="rounded border border-rule-soft bg-bg px-2 py-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-link">{s.registry}</span>
                      <span className="text-link">{s.name}</span>
                      {s.version ? <span className="font-mono text-dim">{s.version}</span> : null}
                      <span className="ml-auto rounded bg-bg px-1.5 py-0.5 text-3xs font-medium text-link">
                        {s.status}
                      </span>
                    </div>
                    {s.note ? <p className="mt-0.5 text-3xs text-dim">{s.note}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.verified.length === 0 && result.skipped.length === 0 && (
            <p className="text-dim">No candidates found in repository manifests.</p>
          )}
        </div>
        <div className="flex justify-end border-t border-rule-soft px-5 py-3">
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'amber' | 'emerald' | 'neutral';
}) {
  const toneClass =
    tone === 'amber'
      ? 'bg-warn/15 text-warn'
      : tone === 'emerald'
        ? 'bg-good/15 text-good'
        : 'bg-bg text-link';
  return (
    <div className={`px-2.5 py-1.5 ${toneClass}`}>
      <div className="text-3xs font-medium uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function AdminVersionRow({
  pkg,
  adminToken,
  onAliasesChanged,
  onAliasError,
  onReindex,
  onIngest,
  onUpdateOverview,
  onToggleHidden,
  onDelete,
  busy,
  reindexing,
  ingesting,
  updatingOverview,
  vectorCount,
}: {
  pkg: PackageSummary;
  adminToken: string | null;
  onAliasesChanged: () => Promise<void>;
  onAliasError: (message: string) => void;
  onReindex: () => void;
  onIngest: () => void;
  onUpdateOverview: () => void;
  onToggleHidden: () => void;
  onDelete: () => void;
  busy: boolean;
  reindexing: boolean;
  ingesting: boolean;
  updatingOverview: boolean;
  vectorCount?: number;
}) {
  const [showReasoning, setShowReasoning] = useState(false);
  // Docs-only indexes (no package source indexed) are a degraded state — always
  // red with an explicit label, regardless of the numeric score.
  const isDocsOnly = isDocsOnlyQuality(pkg.index_quality_note);
  const qualityTone =
    pkg.index_quality == null
      ? null
      : isDocsOnly
        ? 'bg-del/15 text-del'
        : pkg.index_quality >= 80
          ? 'bg-good/15 text-good'
          : pkg.index_quality >= 50
            ? 'bg-warn/15 text-warn'
            : 'bg-del/15 text-del';
  return (
    <div className="space-y-2 border border-rule-soft bg-bg px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <span
          className={`font-mono font-medium ${pkg.hidden ? 'line-through text-dim' : 'text-accent'}`}
        >
          {pkg.display_version ?? pkg.version}
        </span>
        {pkg.hidden && (
          <span className="rounded bg-warn/15 px-1 py-0.5 text-3xs font-medium text-warn">
            hidden
          </span>
        )}
        {pkg.index_quality != null && qualityTone && (
          <button
            type="button"
            onClick={() => setShowReasoning((v) => !v)}
            disabled={!pkg.index_quality_note}
            title={pkg.index_quality_note ? 'Click to see reasoning' : 'No reasoning stored'}
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-3xs font-medium transition-colors ${qualityTone} ${pkg.index_quality_note ? 'cursor-pointer hover:brightness-95 dark:hover:brightness-110' : 'cursor-default opacity-80'}`}
          >
            {pkg.index_quality}% {isDocsOnly ? 'docs-only' : 'quality'}
            {pkg.index_quality_note && (
              <span className="text-3xs opacity-70">{showReasoning ? '▾' : '▸'}</span>
            )}
          </button>
        )}
        {pkg.docs_url && (
          <a
            href={pkg.docs_url}
            target="_blank"
            rel="noopener noreferrer"
            title={`Docs indexed: ${pkg.docs_url}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent hover:bg-accent/20"
          >
            <svg
              aria-hidden="true"
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            docs
          </a>
        )}
        {vectorCount != null && vectorCount > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded bg-good/15 px-1.5 py-0.5 text-3xs font-medium text-good"
            title={`${vectorCount} vectors in DB`}
          >
            <DatabaseIcon className="h-3 w-3" />
            {vectorCount} vectors
          </span>
        )}
        {pkg.released_at && (
          <span className="text-3xs text-dim" title={formatIndexedAt(pkg.released_at)}>
            released {relativeTime(pkg.released_at)}
          </span>
        )}
        <span className="text-3xs text-dim" title={formatIndexedAt(pkg.indexed_at)}>
          indexed {relativeTime(pkg.indexed_at)}
        </span>
        {pkg.index_started_at && pkg.index_completed_at && (
          <span
            className="rounded bg-accent-soft px-1.5 py-0.5 text-3xs font-medium text-accent"
            title={`${formatIndexedAt(pkg.index_started_at)} → ${formatIndexedAt(pkg.index_completed_at)}`}
          >
            {formatDuration(
              new Date(pkg.index_completed_at).getTime() - new Date(pkg.index_started_at).getTime()
            )}{' '}
            to index
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={onReindex}
            disabled={busy || reindexing}
            title="Reindex this version"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-dim hover:bg-accent-soft hover:text-accent disabled:opacity-40 transition-colors"
          >
            {reindexing ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                Queued…
              </>
            ) : (
              <>
                <ReindexIcon />
                Reindex
              </>
            )}
          </button>
          <button
            onClick={onIngest}
            disabled={busy || ingesting}
            title="Ingest vectors for search"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-dim hover:bg-good/15 hover:text-good disabled:opacity-40 transition-colors"
          >
            {ingesting ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-good border-t-transparent" />
                Ingesting…
              </>
            ) : (
              <>
                <UploadIcon />
                Ingest
              </>
            )}
          </button>
          <button
            onClick={onUpdateOverview}
            disabled={busy || updatingOverview}
            title="Regenerate repository overview"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-dim hover:bg-accent-soft hover:text-accent disabled:opacity-40 transition-colors"
          >
            {updatingOverview ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                Updating…
              </>
            ) : (
              <>
                <OverviewIcon />
                Overview
              </>
            )}
          </button>
          <button
            onClick={onToggleHidden}
            disabled={busy}
            title={pkg.hidden ? 'Show this version' : 'Hide this version'}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-dim hover:bg-warn/15 hover:text-warn disabled:opacity-40 transition-colors"
          >
            {pkg.hidden ? (
              <>
                <EyeIcon /> Show
              </>
            ) : (
              <>
                <EyeOffIcon /> Hide
              </>
            )}
          </button>
          {/* Separated from the reversible actions above: this one is not. */}
          <button
            onClick={onDelete}
            disabled={busy || !pkg.id}
            title={pkg.id ? 'Permanently delete this version' : 'Package id unavailable'}
            className="ml-1 inline-flex items-center gap-1 rounded border-l border-rule-soft pl-2 px-1.5 py-0.5 text-dim hover:bg-del/15 hover:text-del disabled:opacity-40 transition-colors"
          >
            <TrashIcon /> Delete
          </button>
        </div>
      </div>

      {showReasoning && pkg.index_quality_note && (
        <div className="rounded border border-rule bg-panel px-2.5 py-2 text-2xs leading-relaxed text-link">
          <p className="mb-1 text-3xs font-medium uppercase tracking-wide text-dim">
            Index quality reasoning
          </p>
          <p className="whitespace-pre-wrap">{pkg.index_quality_note}</p>
        </div>
      )}

      <AliasManager
        pkg={pkg}
        adminToken={adminToken}
        onChanged={onAliasesChanged}
        onError={onAliasError}
      />
    </div>
  );
}

function PackageCard({
  group,
  shade,
  isAdmin,
  adminToken,
  onAdminAction,
  onAliasesChanged,
  onAliasError,
  ingestStatus,
  selected,
  onToggleSelect,
  health,
  onPauseToggle,
}: PackageCardProps) {
  const latest = group.versions[0];
  const params = { registry: latest.registry, name: latest.name, version: latest.version };
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [ingestingKey, setIngestingKey] = useState<string | null>(null);
  const [overviewKey, setOverviewKey] = useState<string | null>(null);
  const [localIngestStatus, setLocalIngestStatus] = useState<Record<string, number>>({});
  // Deletion is confirmed in a dialog rather than through handleAction's busy
  // keys: it needs its own in-flight and error state so a failure stays visible
  // in the dialog instead of dismissing it.
  const [pendingDelete, setPendingDelete] = useState<PackageSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pauseBusy, setPauseBusy] = useState(false);
  const paused = Boolean(health?.paused_at);

  const togglePause = async () => {
    if (!onPauseToggle) return;
    setPauseBusy(true);
    try {
      await onPauseToggle();
    } finally {
      setPauseBusy(false);
    }
  };

  const handleAction = async (action: AdminAction) => {
    const key =
      action.type === 'reindex'
        ? `reindex-${action.version}`
        : action.type === 'ingest'
          ? `ingest-${action.version}`
          : action.type === 'overview'
            ? `overview-${action.version}`
            : `hide-${action.version}`;
    if (action.type === 'ingest') {
      setIngestingKey(key);
    } else if (action.type === 'overview') {
      setOverviewKey(key);
    } else {
      setBusyKey(key);
    }
    try {
      const result = await onAdminAction(action);
      if (action.type === 'ingest' && typeof result === 'number') {
        const pkgId = `${action.registry}:${action.name}:${action.version}`;
        setLocalIngestStatus((prev) => ({ ...prev, [pkgId]: result }));
      }
    } finally {
      if (action.type === 'ingest') {
        setIngestingKey(null);
      } else if (action.type === 'overview') {
        setOverviewKey(null);
      } else {
        setBusyKey(null);
      }
    }
  };

  const getVectorCount = (v: PackageSummary) => {
    const pkgId = `${v.registry}:${v.name}:${v.version}`;
    return localIngestStatus[pkgId] ?? ingestStatus[pkgId];
  };

  return (
    <div className={`transition-colors ${shade ? 'bg-bg' : 'bg-panel'}`}>
      <div className={`flex items-center gap-4 px-5 py-3 hover:bg-accent-soft transition-colors`}>
        {isAdmin && onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-rule text-accent focus:ring-accent"
            aria-label={`Select ${group.name}`}
          />
        )}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <RegistryBadge registry={latest.registry} />
          <span
            className={`text-sm font-semibold truncate ${latest.hidden ? 'line-through text-dim' : 'text-ink'}`}
          >
            {displayPackageName(group.registry, group.name)}
          </span>
          {latest.hidden && (
            <span className="shrink-0 rounded bg-warn/15 px-1.5 py-0.5 text-3xs font-medium text-warn">
              hidden
            </span>
          )}
          {isAdmin && paused && (
            <span
              className="shrink-0 rounded bg-warn/15 px-1.5 py-0.5 text-3xs font-medium text-warn"
              title={`Automatic indexing paused${health?.paused_by === 'auto' ? ' automatically' : ` by ${health?.paused_by}`}: ${health?.pause_reason ?? ''}`}
            >
              paused
            </span>
          )}
          {isAdmin && !paused && (health?.consecutive_failures ?? 0) > 0 && (
            <span
              className="shrink-0 rounded bg-del/15 px-1.5 py-0.5 text-3xs font-medium text-del"
              title={`Last failure: ${health?.last_failure ?? 'unknown'}`}
            >
              {health?.consecutive_failures} failing
            </span>
          )}
          <span className="text-xs text-dim truncate hidden sm:flex items-center gap-1.5">
            <span
              className={`${latest.hidden ? 'line-through opacity-50' : 'text-accent font-medium'} ${getVectorCount(latest) ? 'underline decoration-good decoration-2 underline-offset-2' : ''}`}
              title={getVectorCount(latest) ? `${getVectorCount(latest)} vectors` : undefined}
            >
              {latest.version}
            </span>
            {isAdmin && group.versions.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setAdminExpanded(true);
                }}
                title={`${group.versions.length} versions — click to view all`}
                className="shrink-0 rounded bg-accent-soft px-1.5 py-0.5 text-3xs font-medium text-accent hover:brightness-95 dark:hover:brightness-110"
              >
                {group.versions.length} versions
              </button>
            )}
          </span>
        </div>

        <span
          className="shrink-0 text-xs text-dim hidden md:block"
          title={`Last indexed ${formatIndexedAt(groupLastIndexedAt(group))}`}
        >
          {relativeTime(groupLastIndexedAt(group))}
        </span>

        {latest.index_quality != null && (
          <span
            className={`shrink-0 rounded px-1.5 py-0.5 text-3xs font-medium hidden sm:block ${
              latest.index_quality >= 80
                ? 'bg-good/15 text-good'
                : latest.index_quality >= 50
                  ? 'bg-warn/15 text-warn'
                  : 'bg-del/15 text-del'
            }`}
            title={
              latest.index_quality_note
                ? `${latest.index_quality}% — ${latest.index_quality_note}`
                : `Index quality: ${latest.index_quality}%`
            }
          >
            {latest.index_quality}% cov
          </span>
        )}

        {latest.index_cost != null && (
          <span
            className="shrink-0 rounded bg-accent-soft px-1.5 py-0.5 text-3xs font-medium text-accent hidden sm:block"
            title={`LLM index cost: $${latest.index_cost.toFixed(4)}`}
          >
            $
            {latest.index_cost < 0.01 ? latest.index_cost.toFixed(4) : latest.index_cost.toFixed(2)}
          </span>
        )}

        <div className="flex shrink-0 items-center gap-1">
          {isAdmin && (
            <button
              onClick={() =>
                void handleAction({
                  type: 'reindex',
                  registry: latest.registry,
                  name: latest.name,
                  version: latest.version,
                })
              }
              disabled={busyKey === `reindex-${latest.version}`}
              title={`Reindex latest (${latest.version})`}
              className="rounded p-1 text-dim hover:bg-accent-soft hover:text-accent disabled:opacity-40 transition-colors"
            >
              {busyKey === `reindex-${latest.version}` ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
              ) : (
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              )}
            </button>
          )}
          {isAdmin && onPauseToggle && (
            <button
              onClick={() => void togglePause()}
              disabled={pauseBusy}
              title={
                paused
                  ? 'Resume automatic indexing of this package'
                  : 'Pause automatic indexing of this package (every version) and stop its queued runs'
              }
              aria-label={paused ? 'Resume automatic indexing' : 'Pause automatic indexing'}
              className={`rounded p-1 transition-colors disabled:opacity-40 ${paused ? 'text-warn hover:bg-warn/15' : 'text-dim hover:bg-warn/15 hover:text-warn'}`}
            >
              {pauseBusy ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-warn border-t-transparent" />
              ) : paused ? (
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              ) : (
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                </svg>
              )}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setAdminExpanded((x) => !x)}
              title="Admin actions"
              className={`rounded p-1 transition-colors ${adminExpanded ? 'bg-accent-soft text-accent' : 'text-dim hover:bg-bg hover:text-link'}`}
            >
              <GearIcon />
            </button>
          )}
          <button
            onClick={() => navigateTo(buildTreeRoute(params))}
            title="Browse"
            className="rounded p-1.5 text-dim hover:bg-accent-soft hover:text-accent transition-colors"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Admin expandable version panel */}
      {isAdmin && adminExpanded && (
        <div className="border-t border-rule-soft bg-bg px-5 py-3 space-y-2">
          <p className="text-2xs font-medium uppercase tracking-wide text-dim mb-2">Versions</p>
          {group.versions.map((v) => (
            <AdminVersionRow
              key={v.version}
              pkg={v}
              adminToken={adminToken}
              onAliasesChanged={onAliasesChanged}
              onAliasError={onAliasError}
              busy={busyKey !== null}
              reindexing={busyKey === `reindex-${v.version}`}
              ingesting={ingestingKey === `ingest-${v.version}`}
              updatingOverview={overviewKey === `overview-${v.version}`}
              vectorCount={getVectorCount(v)}
              onReindex={() =>
                void handleAction({
                  type: 'reindex',
                  registry: v.registry,
                  name: v.name,
                  version: v.version,
                })
              }
              onIngest={() =>
                void handleAction({
                  type: 'ingest',
                  registry: v.registry,
                  name: v.name,
                  version: v.version,
                })
              }
              onUpdateOverview={() =>
                void handleAction({
                  type: 'overview',
                  registry: v.registry,
                  name: v.name,
                  version: v.version,
                })
              }
              onToggleHidden={() =>
                void handleAction({
                  type: 'hide',
                  registry: v.registry,
                  name: v.name,
                  version: v.version,
                  hidden: !v.hidden,
                })
              }
              onDelete={() => setPendingDelete(v)}
            />
          ))}
        </div>
      )}

      {pendingDelete && (
        <DeleteVersionDialog
          pkg={pendingDelete}
          busy={deleting}
          error={deleteError}
          onClose={() => {
            setPendingDelete(null);
            setDeleteError(null);
          }}
          onConfirm={() => {
            if (!pendingDelete.id) {
              setDeleteError('This package row has no id, so it cannot be deleted safely.');
              return;
            }
            setDeleting(true);
            setDeleteError(null);
            void (async () => {
              try {
                await onAdminAction({
                  type: 'delete',
                  packageId: pendingDelete.id!,
                  registry: pendingDelete.registry,
                  name: pendingDelete.name,
                  version: pendingDelete.version,
                });
                setPendingDelete(null);
              } catch (err) {
                setDeleteError(err instanceof Error ? err.message : 'Delete failed');
              } finally {
                setDeleting(false);
              }
            })();
          }}
        />
      )}
    </div>
  );
}

interface AdminOverviewStats {
  pendingApprovals: number;
  failed: number;
  zeroVectors: number;
  indexedToday: number;
  averageIndexTimeMs: number | null;
  registryStats: Array<{ registry: string; count: number; averageIndexTimeMs: number | null }>;
  todaysCost: number;
  weekCost: number;
  totalCost: number;
}

function formatCostCompact(value: number): string {
  if (value === 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

function StatTile({
  label,
  value,
  tone,
  onClick,
  hint,
}: {
  label: string;
  value: string | number;
  tone: 'neutral' | 'amber' | 'red' | 'emerald' | 'violet';
  onClick?: () => void;
  hint?: string;
}) {
  const toneStyles: Record<typeof tone, string> = {
    neutral: 'text-ink',
    amber: 'text-warn',
    red: 'text-del',
    emerald: 'text-good',
    violet: 'text-accent',
  };
  const interactive = onClick ? 'cursor-pointer hover:border-accent' : '';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`border border-rule bg-panel p-3 text-left transition-colors ${interactive} disabled:cursor-default`}
    >
      <p className="text-3xs font-medium uppercase tracking-wider text-dim">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${toneStyles[tone]}`}>{value}</p>
      {hint ? <p className="mt-0.5 text-2xs text-dim">{hint}</p> : null}
    </button>
  );
}

function AdminOverviewStrip({
  stats,
  onPendingClick,
  onFailedClick,
  onNoVectorsClick,
}: {
  stats: AdminOverviewStats;
  onPendingClick: () => void;
  onFailedClick: () => void;
  onNoVectorsClick: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
        <StatTile
          label="Pending approvals"
          value={stats.pendingApprovals}
          tone={stats.pendingApprovals > 0 ? 'amber' : 'neutral'}
          onClick={stats.pendingApprovals > 0 ? onPendingClick : undefined}
          hint={stats.pendingApprovals > 0 ? 'Review below' : 'All clear'}
        />
        <StatTile
          label="Failed indexings"
          value={stats.failed}
          tone={stats.failed > 0 ? 'red' : 'neutral'}
          onClick={stats.failed > 0 ? onFailedClick : undefined}
          hint={stats.failed > 0 ? 'Retry below' : 'None'}
        />
        <StatTile
          label="No vectors"
          value={stats.zeroVectors}
          tone={stats.zeroVectors > 0 ? 'amber' : 'neutral'}
          onClick={stats.zeroVectors > 0 ? onNoVectorsClick : undefined}
          hint={stats.zeroVectors > 0 ? 'Filter below' : 'All indexed'}
        />
        <StatTile
          label="Indexed today"
          value={stats.indexedToday}
          tone="emerald"
          hint="Completed versions"
        />
        <StatTile
          label="Avg index time"
          value={stats.averageIndexTimeMs === null ? '—' : formatDuration(stats.averageIndexTimeMs)}
          tone="emerald"
          hint="Today's completed runs"
        />
        <StatTile
          label="Today's cost"
          value={formatCostCompact(stats.todaysCost)}
          tone="violet"
          hint="Completed today"
        />
        <StatTile
          label="This week's cost"
          value={formatCostCompact(stats.weekCost)}
          tone="violet"
          hint="Since Sunday"
        />
        <StatTile
          label="Total cost"
          value={formatCostCompact(stats.totalCost)}
          tone="violet"
          hint="All indexed versions"
        />
      </div>
      {stats.registryStats.length > 0 ? (
        <div className="border border-rule bg-panel px-4 py-3" data-testid="today-registry-stats">
          <p className="text-3xs font-medium uppercase tracking-wider text-dim">
            Today by registry
          </p>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
            {stats.registryStats.map((registry) => (
              <div key={registry.registry} className="flex items-baseline gap-2 text-xs">
                <span className="font-medium text-ink">{registry.registry}</span>
                <span className="font-mono text-dim">
                  {registry.count} indexed ·{' '}
                  {registry.averageIndexTimeMs === null
                    ? 'time unavailable'
                    : `${formatDuration(registry.averageIndexTimeMs)} avg`}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function PackagesListPage({ isAdmin = false }: { isAdmin?: boolean }) {
  const { user, getToken } = useAuth();

  const [packages, setPackages] = useState<PackageSummary[]>([]);
  const [requests, setRequests] = useState<IndexRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(25);
  const [adminToken, setAdminToken] = useState<string | null>(null);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [ingestStatus, setIngestStatus] = useState<Record<string, number>>({});
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const [noVectorsOnly, setNoVectorsOnly] = useState(false);
  const [groupByRegistry, setGroupByRegistry] = useState(false);
  const [registryFilter, setRegistryFilter] = useState<string>('all');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [bulkReindexing, setBulkReindexing] = useState(false);
  const [pendingBulkReindex, setPendingBulkReindex] = useState(false);
  const [packageHealth, setPackageHealth] = useState<PackageIndexHealth[] | null>(null);
  const [packageHealthError, setPackageHealthError] = useState<string | null>(null);
  const approvalsRef = useRef<HTMLDivElement>(null);

  // Fetch admin token when user logs in
  useEffect(() => {
    if (!user) {
      setAdminToken(null);
      return;
    }
    void getToken().then(setAdminToken);
  }, [user, getToken]);

  const refreshPackageHealth = useCallback(async () => {
    if (!isAdmin || !adminToken) return;
    try {
      setPackageHealth(await fetchPackageHealth(adminToken));
      setPackageHealthError(null);
    } catch (err) {
      setPackageHealthError(err instanceof Error ? err.message : 'Failed to load package health');
    }
  }, [isAdmin, adminToken]);

  useEffect(() => {
    void refreshPackageHealth();
  }, [refreshPackageHealth]);

  // Public packages only: the catalog groups by registry and name across orgs,
  // so a card stands for the public package. Org pauses live in the panel.
  const healthByPackage = useMemo(() => {
    const map = new Map<string, PackageIndexHealth>();
    for (const row of packageHealth ?? []) {
      if (!row.org_id) map.set(healthKey(row.registry, row.name), row);
    }
    return map;
  }, [packageHealth]);

  // Pausing fails the package's queued and running requests, so the queue is
  // refreshed too. Errors propagate to the caller (panel or card) to show.
  const pausePackage = useCallback(
    async (key: PackagePauseKey, reason: string) => {
      if (!adminToken) return;
      await adminPausePackage({ ...key, reason: reason || undefined }, adminToken);
      await refreshPackageHealth();
      void fetchIndexRequests()
        .then(setRequests)
        .catch(() => {
          /* ignore */
        });
    },
    [adminToken, refreshPackageHealth]
  );

  const resumePackage = useCallback(
    async (key: PackagePauseKey) => {
      if (!adminToken) return;
      await adminResumePackage(key, adminToken);
      await refreshPackageHealth();
    },
    [adminToken, refreshPackageHealth]
  );

  const togglePackagePause = useCallback(
    async (registry: string, name: string) => {
      const key = { registry, name, orgId: null };
      setAdminError(null);
      try {
        if (healthByPackage.get(healthKey(registry, name))?.paused_at) await resumePackage(key);
        else await pausePackage(key, '');
      } catch (err) {
        setAdminError(err instanceof Error ? err.message : 'Pause failed');
      }
    },
    [healthByPackage, pausePackage, resumePackage]
  );

  // Fetch ingest status for all users (public endpoint)
  useEffect(() => {
    void fetchIngestStatus()
      .then(setIngestStatus)
      .catch(() => {
        /* silently ignore */
      });
  }, []);

  // Poll index requests for the overview strip and approvals panel
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = () => {
      void fetchIndexRequests()
        .then((next) => {
          if (!cancelled) setRequests(next);
        })
        .catch(() => {
          /* silently ignore */
        });
    };
    load();
    const interval = setInterval(load, 10_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAdmin]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const nextPackages = adminToken
        ? await fetchAdminPackages(adminToken)
        : await fetchPackages();
      setPackages(nextPackages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load indexed repositories.');
    } finally {
      setIsLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const allGroups = useMemo(() => groupPackages(packages), [packages]);

  const availableRegistries = useMemo(() => {
    const set = new Set<string>();
    for (const g of allGroups) set.add(g.registry);
    return [...set].sort(
      (a, b) => registrySectionRank(a) - registrySectionRank(b) || a.localeCompare(b)
    );
  }, [allGroups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allGroups.filter((g) => {
      if (q && !g.name.toLowerCase().includes(q)) return false;
      if (registryFilter !== 'all' && g.registry !== registryFilter) return false;
      if (hiddenOnly && !g.versions.some((v) => v.hidden)) return false;
      if (noVectorsOnly) {
        const latest = g.versions[0];
        const key = `${latest.registry}:${latest.name}:${latest.version}`;
        if ((ingestStatus[key] ?? 0) > 0) return false;
      }
      return true;
    });
  }, [allGroups, search, registryFilter, hiddenOnly, noVectorsOnly, ingestStatus]);
  const sorted = useMemo(() => sortGroups(filtered, sort), [filtered, sort]);

  const filtersActive =
    Boolean(search.trim()) || registryFilter !== 'all' || hiddenOnly || noVectorsOnly;

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paginated = sorted.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, sort, pageSize, registryFilter, hiddenOnly, noVectorsOnly]);

  const toggleSelection = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  const selectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const g of filtered) next.add(`${g.registry}:${g.name}`);
      return next;
    });
  }, [filtered]);

  const runBulkReindex = useCallback(async () => {
    if (!adminToken || selectedKeys.size === 0) return;
    setBulkReindexing(true);
    setAdminError(null);
    try {
      const targets = Array.from(selectedKeys)
        .map((key) => {
          const group = allGroups.find((g) => `${g.registry}:${g.name}` === key);
          return group ? group.versions[0] : null;
        })
        .filter((v): v is PackageSummary => v !== null);
      await Promise.all(
        targets.map((v) =>
          adminReindex({ registry: v.registry, name: v.name, version: v.version }, adminToken)
        )
      );
      clearSelection();
      void fetchIndexRequests()
        .then(setRequests)
        .catch(() => {
          /* ignore */
        });
      requestAnimationFrame(() => {
        approvalsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Bulk reindex failed');
    } finally {
      setBulkReindexing(false);
      setPendingBulkReindex(false);
    }
  }, [adminToken, selectedKeys, allGroups, clearSelection]);

  const registrySections = useMemo(() => {
    const grouped = new Map<string, PackageGroup[]>();
    for (const group of sorted) {
      const bucket = grouped.get(group.registry) ?? [];
      bucket.push(group);
      grouped.set(group.registry, bucket);
    }
    return [...grouped.entries()]
      .sort(
        (left, right) =>
          registrySectionRank(left[0]) - registrySectionRank(right[0]) ||
          left[0].localeCompare(right[0])
      )
      .map(([registry, groups]) => ({ registry, label: registry, groups }));
  }, [sorted]);

  const handleAdminAction = useCallback(
    async (action: AdminAction): Promise<number | void> => {
      if (!adminToken) return;
      setAdminError(null);
      try {
        if (action.type === 'reindex') {
          await adminReindex(
            { registry: action.registry, name: action.name, version: action.version },
            adminToken
          );
          void fetchIndexRequests()
            .then(setRequests)
            .catch(() => {
              /* ignore */
            });
          requestAnimationFrame(() => {
            approvalsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        } else if (action.type === 'ingest') {
          const result = await adminIngest(
            { registry: action.registry, name: action.name, version: action.version },
            adminToken
          );
          return result.ingested;
        } else if (action.type === 'overview') {
          await adminUpdateOverview(
            { registry: action.registry, name: action.name, version: action.version },
            adminToken
          );
        } else if (action.type === 'delete') {
          await adminDeletePackage(action.packageId, adminToken);
          // Drop it locally rather than refetching the whole catalog: the row is
          // gone, and leaving it on screen until the next load invites a second
          // delete against an id that no longer exists.
          setPackages((prev) => prev.filter((p) => p.id !== action.packageId));
        } else {
          await adminSetHidden(
            {
              registry: action.registry,
              name: action.name,
              version: action.version,
              hidden: action.hidden,
            },
            adminToken
          );
          // Optimistically update local state
          setPackages((prev) =>
            prev.map((p) =>
              p.registry === action.registry &&
              p.name === action.name &&
              p.version === action.version
                ? { ...p, hidden: action.hidden }
                : p
            )
          );
        }
      } catch (err) {
        setAdminError(err instanceof Error ? err.message : 'Admin action failed');
        // Delete is confirmed in a dialog that reports its own failures and
        // stays open on one. Swallowing here would close it as though the
        // version had been deleted, and only a page-level banner would say
        // otherwise — for the single action here that cannot be retried by
        // eye, that is the wrong way round.
        if (action.type === 'delete') throw err;
      }
    },
    [adminToken]
  );

  const handleAliasesChanged = useCallback(async () => {
    await load();
  }, [load]);

  const liveVersionCount = packages.filter((p) => !p.hidden).length;
  const hiddenVersionCount = packages.length - liveVersionCount;
  const showControls = !isLoading && allGroups.length > 0;

  const overviewStats = useMemo(() => {
    const pendingApprovals = requests.filter((r) => r.status === 'pending_approval').length;

    // Failed indexings that still need attention. `requests` is capped at the
    // 50 most recent and each retry is its own row, so counting raw failed rows
    // over-counts. Dedupe by package (plus version when the request pins one)
    // and drop any that has since been indexed successfully.
    const indexedVersions = new Set(packages.map((p) => `${p.registry}:${p.name}:${p.version}`));
    const indexedNames = new Set(packages.map((p) => `${p.registry}:${p.name}`));
    const failedKeys = new Set<string>();
    for (const r of requests) {
      if (r.status !== 'failed') continue;
      const nameKey = `${r.registry}:${r.name}`;
      if (r.version) {
        const verKey = `${nameKey}:${r.version}`;
        if (!indexedVersions.has(verKey)) failedKeys.add(verKey);
      } else if (!indexedNames.has(nameKey)) {
        failedKeys.add(nameKey);
      }
    }
    const failed = failedKeys.size;

    // Repository-level, to match the "No vectors" filter and the tile hint:
    // count repos whose latest (highest-semver) version has no vectors yet.
    const zeroVectors = allGroups.filter((g) => {
      const latest = g.versions[0];
      if (latest.hidden) return false;
      const key = `${latest.registry}:${latest.name}:${latest.version}`;
      return (ingestStatus[key] ?? 0) === 0;
    }).length;

    // Cost of everything indexed today/this week, from the full package list
    // rather than the capped requests window (which undercounts on busy
    // days). Local day/week boundary so they match the admin's wall clock.
    // Every version is its own indexing run with its own cost, so summing
    // across all package rows (not just the latest per group) is correct —
    // matches how index_cost is billed.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
    const todayIndexing = calculateTodayIndexingStats(packages);
    let todaysCost = 0;
    let weekCost = 0;
    let totalCost = 0;
    for (const p of packages) {
      const indexedAt = new Date(p.indexed_at).getTime();
      const completedToday = indexedAt >= startOfToday.getTime();
      if (typeof p.index_cost === 'number') {
        totalCost += p.index_cost;
        if (indexedAt >= startOfWeek.getTime()) weekCost += p.index_cost;
        if (completedToday) todaysCost += p.index_cost;
      }
    }

    return {
      pendingApprovals,
      failed,
      zeroVectors,
      ...todayIndexing,
      todaysCost,
      weekCost,
      totalCost,
    };
  }, [requests, packages, allGroups, ingestStatus]);

  const scrollToApprovals = () => {
    approvalsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink">Repositories</h2>
          <p className="text-sm text-dim">
            {allGroups.length} {allGroups.length === 1 ? 'repository' : 'repositories'} ·{' '}
            {liveVersionCount} version{liveVersionCount !== 1 ? 's' : ''} indexed
            {hiddenVersionCount > 0 ? ` · ${hiddenVersionCount} hidden` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => navigateTo('/request')}>
            Request index
          </Button>
        </div>
      </div>

      {/* Global Ask Bar */}
      <GlobalAskBar />

      {isAdmin && (
        <AdminOverviewStrip
          stats={overviewStats}
          onPendingClick={scrollToApprovals}
          onFailedClick={scrollToApprovals}
          onNoVectorsClick={() => setNoVectorsOnly(true)}
        />
      )}

      {isAdmin && (
        <div ref={approvalsRef}>
          <CommonPackageRequestsPanel
            sectionId="admin-approvals"
            isAdmin
            hideForm
            title="Approvals & indexing queue"
            filter={(r) =>
              r.status === 'pending_approval' ||
              r.status === 'pending' ||
              r.status === 'indexing' ||
              r.status === 'failed'
            }
            emptyLabel="Nothing awaiting approval or in the queue."
          />
        </div>
      )}

      {isAdmin && adminToken && (
        <PackageHealthPanel
          rows={packageHealth}
          error={packageHealthError}
          onPause={pausePackage}
          onResume={resumePackage}
        />
      )}

      {adminToken && <IndexMetricsPanel />}

      {adminToken && <AnalyticsPanel />}

      {adminError && (
        <div className="border border-del/30 bg-del/15 px-4 py-2 text-xs text-del">
          {adminError}
        </div>
      )}

      {/* Search + sort toolbar */}
      {showControls && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <SearchIcon />
              </span>
              <input
                className="lc-input pl-9"
                type="search"
                placeholder="Filter repositories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {isAdmin && availableRegistries.length > 1 && (
              <select
                className="lc-input w-auto cursor-pointer"
                value={registryFilter}
                onChange={(e) => setRegistryFilter(e.target.value)}
              >
                <option value="all">All registries</option>
                {availableRegistries.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
            <select
              className="lc-input w-auto cursor-pointer"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
            >
              <option value="name-asc">Name A→Z</option>
              <option value="name-desc">Name Z→A</option>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="versions">Most versions</option>
              {isAdmin && <option value="cost-high">Cost high→low</option>}
              {isAdmin && <option value="cost-low">Cost low→high</option>}
              {isAdmin && <option value="quality-high">Quality high→low</option>}
              {isAdmin && <option value="quality-low">Quality low→high</option>}
            </select>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 text-xs">
              <FilterChip active={hiddenOnly} onClick={() => setHiddenOnly((v) => !v)}>
                Hidden only
              </FilterChip>
              <FilterChip active={noVectorsOnly} onClick={() => setNoVectorsOnly((v) => !v)}>
                No vectors
              </FilterChip>
              <FilterChip active={groupByRegistry} onClick={() => setGroupByRegistry((v) => !v)}>
                Group by registry
              </FilterChip>
              {filtersActive && (
                <button
                  type="button"
                  className="ml-1 text-dim hover:text-link"
                  onClick={() => {
                    setSearch('');
                    setRegistryFilter('all');
                    setHiddenOnly(false);
                    setNoVectorsOnly(false);
                  }}
                >
                  Clear filters
                </button>
              )}
              <span className="ml-auto text-dim">
                {filtered.length} match{filtered.length === 1 ? '' : 'es'}
              </span>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="lc-surface p-6 text-sm text-dim">Loading…</div>
      ) : allGroups.length === 0 ? (
        <div className="lc-surface p-12 text-center text-sm text-dim">
          No repositories indexed yet. Click "Request index" to get started.
        </div>
      ) : sorted.length === 0 ? (
        <div className="lc-surface p-8 text-center text-sm text-dim">
          No repositories match <span className="font-medium text-link">"{search}"</span>
        </div>
      ) : (
        <>
          {isAdmin && selectedKeys.size > 0 && (
            <div className="sticky top-2 z-20 flex items-center justify-between gap-3 border border-accent bg-accent-soft px-4 py-2">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-accent">{selectedKeys.size} selected</span>
                <button
                  type="button"
                  className="text-xs text-accent hover:underline"
                  onClick={selectAllVisible}
                >
                  Select all filtered ({filtered.length})
                </button>
                <button
                  type="button"
                  className="text-xs text-dim hover:underline"
                  onClick={clearSelection}
                >
                  Clear
                </button>
              </div>
              <Button
                size="sm"
                onClick={() => setPendingBulkReindex(true)}
                disabled={bulkReindexing}
              >
                {bulkReindexing ? 'Reindexing…' : `Reindex ${selectedKeys.size}`}
              </Button>
            </div>
          )}
          {groupByRegistry ? (
            <div className="space-y-4">
              {registrySections.map((section) => (
                <div key={section.registry} className="lc-surface overflow-hidden">
                  <div className="flex items-center justify-between border-b border-rule-soft px-5 py-3">
                    <div className="flex items-center gap-2">
                      <RegistryBadge registry={section.registry} />
                      <h3 className="text-sm font-semibold text-ink">{section.label}</h3>
                    </div>
                    <span className="text-xs text-dim">
                      {section.groups.length}{' '}
                      {section.groups.length === 1 ? 'repository' : 'repositories'}
                    </span>
                  </div>
                  <div className="divide-y divide-rule-soft">
                    {section.groups.map((group, i) => {
                      const selectionKey = `${group.registry}:${group.name}`;
                      return (
                        <PackageCard
                          key={selectionKey}
                          group={group}
                          shade={i % 2 === 1}
                          isAdmin={isAdmin}
                          adminToken={adminToken}
                          onAdminAction={(action) => handleAdminAction(action)}
                          onAliasesChanged={handleAliasesChanged}
                          onAliasError={setAdminError}
                          ingestStatus={ingestStatus}
                          selected={selectedKeys.has(selectionKey)}
                          onToggleSelect={() => toggleSelection(selectionKey)}
                          health={healthByPackage.get(healthKey(group.registry, group.name))}
                          onPauseToggle={() => togglePackagePause(group.registry, group.name)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="lc-surface overflow-hidden">
              <div className="divide-y divide-rule-soft">
                {paginated.map((group, i) => {
                  const selectionKey = `${group.registry}:${group.name}`;
                  return (
                    <PackageCard
                      key={selectionKey}
                      group={group}
                      shade={i % 2 === 1}
                      isAdmin={isAdmin}
                      adminToken={adminToken}
                      onAdminAction={(action) => handleAdminAction(action)}
                      onAliasesChanged={handleAliasesChanged}
                      onAliasError={setAdminError}
                      ingestStatus={ingestStatus}
                      selected={selectedKeys.has(selectionKey)}
                      onToggleSelect={() => toggleSelection(selectionKey)}
                      health={healthByPackage.get(healthKey(group.registry, group.name))}
                      onPauseToggle={() => togglePackagePause(group.registry, group.name)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Pagination (hidden when grouping — grouped view shows all registries at once) */}
          {!groupByRegistry && (
            <div className="flex items-center justify-between text-sm text-dim">
              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  className="lc-input w-auto cursor-pointer py-1 text-xs"
                  value={pageSize}
                  onChange={(e) =>
                    setPageSize(Number(e.target.value) as (typeof PAGE_SIZE_OPTIONS)[number])
                  }
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs">
                  {pageStart + 1}–{Math.min(pageStart + pageSize, sorted.length)} of {sorted.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    className="rounded p-1 hover:bg-bg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={currentPage <= 1}
                    aria-label="Previous page"
                  >
                    <ChevronIcon direction="left" />
                  </button>
                  <span className="min-w-[4rem] text-center text-xs">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    className="rounded p-1 hover:bg-bg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                  >
                    <ChevronIcon direction="right" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {error && (
        <ErrorDialog
          title="Unable to Load Repositories"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {pendingBulkReindex && (
        <ConfirmDialog
          title={`Reindex ${selectedKeys.size} ${selectedKeys.size === 1 ? 'repository' : 'repositories'}?`}
          message="This will queue a fresh index job for the latest version of each selected repository. Existing vectors remain available until the new index completes."
          confirmLabel={`Reindex ${selectedKeys.size}`}
          isConfirming={bulkReindexing}
          onConfirm={() => {
            void runBulkReindex();
          }}
          onClose={() => setPendingBulkReindex(false)}
        />
      )}
    </div>
  );
}
